import {
  deleteDepartment,
  getAllFOSEs,
  getAllVariants,
  getDepartments,
  patchDepartment,
  saveDepartment } from 'frontend/communications/apiClient'
import { getOrdererLevelArray, sortPatternAlphabetically } from 'frontend/helpers/perimeterUtils'
import { usePerimeterStore } from "frontcore/store/usePerimeterStore"

const stateObj = {
  perimeters: [],
  patterns: [],
  departments: [],
  variants: [],
  foses: [],
}

const moduleGetters = {
  getOrderedPatterns (state) {
    const patterns = []
    getOrdererLevelArray(state.patterns, patterns, null, 0)
    return patterns
  },
  getPerimeters (state) {
    return state.perimeters
  },
  getParentByPerimeter: (state) => (perimeter) => {
    if (perimeter.parent == null)  return null
    return state.perimeters.find(perimeterData => perimeterData.id === perimeter.parent)
  },
  getPerimeterById: (state) => (id) => {
    return state.perimeters.find(perimeter => perimeter.id === id)
  },
  getChildrenByPerimeterId: (state) => (id) => {
    return state.perimeters.filter(perimeter => perimeter.parent === id)
  },
  getRecursiveChildrenIdsByPerimeterId: (state, getters) => (id, recursiveChildrensIds=[]) => {
    getters.getChildrenByPerimeterId(id).forEach(children => {
      const childrenId = children.id
      recursiveChildrensIds.push(childrenId)
      if (getters.getChildrenByPerimeterId(childrenId).length > 0) {
        recursiveChildrensIds = getters.getRecursiveChildrenIdsByPerimeterId(
          childrenId, recursiveChildrensIds
        )
      }
    })
    return recursiveChildrensIds
  },
  getFirstChildFromList: (state) => (id, perimeterList) => {
    return state.perimeters.find(perimeter => perimeter.parent === id && perimeterList.includes(perimeter.id))
  },
  getRootPerimeters: (state) => {
    const rootPatternIds = state.patterns.filter((pattern) => {
      return pattern.parent === null
    }).map(pattern => pattern.id)
    return state.perimeters.filter((perimeter) => {
      return rootPatternIds.includes(perimeter.pattern)
    }).map(perimeter => perimeter.id )
  },
  getPatterns (state) {
    return state.patterns
  },
  getPatternById: (state) => (id) => {
    return state.patterns.find(pattern => pattern.id === id)
  },
  getPatternChildrenByPatternId: (state) => (id) => {
    return state.patterns
      .filter(currentPattern => { return id === currentPattern.parent })
      .sort(sortPatternAlphabetically)
  },
  getDepartments (state) {
    return state.departments
  },
  getActiveDepartments (state) {
    return state.departments.filter(department => {return department.enabled})
  },
  departmentsForStandardApprove (state) {
    return state.departments.filter(department => department.for_standard_approve)
  },
  isAnyDepartmentMandatoryForStandardApproval (state, getters) {
    return getters.departmentsForStandardApprove.length > 0
  },
  getDepartmentById: (state) => (id) => {
    return state.departments.find(department => department.id === id)
  },
  getVariants (state) {
    return state.variants
  },
  getVariantById: (state) => (id) => {
    return state.variants.find(variant => variant.id === id)
  },
  getFOSEs (state) {
    return state.foses
  },
}

const mutations = {
  setPerimeters (state, value) {
    state.perimeters = value
  },
  addPerimeter (state, value) {
    state.perimeters.push(value)
  },
  updatePerimeter (state, value) {
    const perimeterIndex = state.perimeters.findIndex(perimeter => perimeter.id === value.id)
    if (perimeterIndex !== -1) {
      state.perimeters.splice(perimeterIndex, 1, value)
    }
  },
  setPatterns (state, value) {
    state.patterns = value
  },
  setDepartments (state, value) {
    state.departments = value
  },
  deletePerimeter (state, id) {
    state.perimeters = state.perimeters
      .filter(perimeter => perimeter.id !== id)
      .filter(perimeter => perimeter.parent === null || perimeter.parent.id !== id)
  },
  setVariants (state, value) {
    state.variants = value
  },
  setFOSEs (state, value) {
    state.foses = value
  },
  createDepartment (state, department) {
    state.departments.push(department)
  },
  updateDepartment (state, department) {
    const index = state.departments.findIndex(d => d.id === department.id)
    const auxDepartments = { ...state.departments[index], ...department }
    state.departments.splice(index, 1, auxDepartments)
  },
  removeDepartment (state, id) {
    state.departments = state.departments
      .filter(department => department.id !== id)
  },
}

const actions = {

  async fetchPerimeters ({ commit }) {
    const perimeterStore = usePerimeterStore()
    await perimeterStore.fetch()
    commit('setPerimeters', perimeterStore.getAllPerimeters)
    return perimeterStore.getAllPerimeters
  },
  async fetchPatterns ({ commit }) {
    const perimeterStore = usePerimeterStore()
    await perimeterStore.fetch()
    commit('setPatterns', perimeterStore.getPatterns)
    return perimeterStore.getPatterns
  },
  async fetchDepartments ({ commit }) {
    const departments = await getDepartments()
    commit('setDepartments', departments)
    return departments
  },
  async fetchVariants ({ commit }) {
    const variants = await getAllVariants(true)
    commit('setVariants', variants)
    return variants
  },
  async fetchFOSEs ({ commit }) {
    const foses = await getAllFOSEs(true)
    commit('setFOSEs', foses)
    return foses
  },
  async createDepartment ({ commit }, payload) {
    const department = await saveDepartment(payload)
    commit('createDepartment', department)
    return department
  },
  async updateDepartment ({ commit }, payload) {
    const id = payload.id
    delete payload.id
    const department = await patchDepartment(id, payload)
    commit('updateDepartment', department)
    return department
  },
  async removeDepartment ({ commit }, id) {
    const department = await deleteDepartment(id)
    commit('removeDepartment', id)
    return department
  },
}

export default {
  state: stateObj,
  getters: moduleGetters,
  mutations,
  actions,
}
